import { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import ExperimentView from "./ExperimentView";
import CHEMISTRY_EXPERIMENTS from "../../../data/chemistryExperiments";
import { useTranslation } from "react-i18next";

export default function ChemistryLab() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { experimentId } = useParams();
  const [searchParams] = useSearchParams();
  const selectedClassParam = searchParams.get("class");

  const [selectedClass, setSelectedClass] = useState(selectedClassParam || "");
  const [progress, setProgress] = useState({});

  const experiments = selectedClass
    ? CHEMISTRY_EXPERIMENTS[selectedClass] || []
    : [];

  // Load saved progress from localStorage
  useEffect(() => {
    const saved = JSON.parse(localStorage.getItem("labProgress") || "{}");
    setProgress(saved);
  }, []);

  const handleCompletion = (experimentId) => {
    const updated = { ...progress, [experimentId]: "completed" };
    setProgress(updated);
    localStorage.setItem("labProgress", JSON.stringify(updated));
  };

  const resetExperiment = (experimentId) => {
    const updated = { ...progress };
    delete updated[experimentId];
    setProgress(updated);
    navigate(
      `/dashboard/student/chemistry/${experimentId}?class=${selectedClass}`
    );
  };

  //  If route param exists (like /chemistry/acid-base), show ExperimentView
  if (experimentId) {
    return (
      <div className="w-full max-w-5xl">
        <ExperimentView
          experimentId={experimentId}
          onBack={() =>
            navigate(`/dashboard/student/chemistry?class=${selectedClass}`)
          }
          onComplete={handleCompletion}
        />
      </div>
    );
  }

  return (
    <div className="min-h-[calc(100vh-120px)] bg-gradient-to-br from-sky-50 to-indigo-50 p-6 flex flex-col items-center">
      {/* Back button (left aligned) */}
      <div className="w-full max-w-5xl flex justify-start mb-4">
        <button
          onClick={() => navigate(`/dashboard/student/virtual-lab`)}
          className="px-4 py-2 bg-gray-200 rounded hover:bg-gray-300"
        >
          {t("chemLab.back")}
        </button>
      </div>

      <h1 className="text-3xl font-bold text-gray-800 mb-6 drop-shadow-sm">
        {t("chemLab.title")}
      </h1>

      {/* Class Selector */}
      <div className="mb-8 flex flex-col sm:flex-row items-center gap-4">
        <label className="text-gray-700 font-medium">
          {t("chemLab.selectClassLabel")}
        </label>
        <select
          value={selectedClass}
          onChange={(e) => {
            setSelectedClass(e.target.value);
            navigate(`/dashboard/student/chemistry?class=${e.target.value}`);
          }}
          className="border border-gray-300 rounded-lg px-4 py-2 text-gray-700 focus:ring-2 focus:ring-blue-400 focus:border-blue-400"
        >
          <option value="">{t("chemLab.chooseClass")}</option>
          <option value="6">{t("virtualLab.class")} 6</option>
          <option value="7">{t("virtualLab.class")} 7</option>
          <option value="8">{t("virtualLab.class")} 8</option>
          <option value="9">{t("virtualLab.class")} 9</option>
          <option value="10">{t("virtualLab.class")} 10</option>
        </select>
      </div>

      {!selectedClass ? (
        <div className="text-gray-500 mt-10 text-center italic">
          {t("chemLab.noClass")}
        </div>
      ) : experiments.length === 0 ? (
        <div className="text-gray-500 mt-10 text-center italic">
          {t("chemLab.noExperiments")}
        </div>
      ) : (
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3 w-full max-w-5xl">
          {experiments.map((exp) => (
            <div
              key={exp.id}
              className="
    rounded-2xl p-6 bg-white border border-blue-100 text-left shadow-md
    transition-all duration-300
    hover:scale-[1.04]
    hover:shadow-[0_0_16px_#3A75C455]
    motion-safe:animate-[fadeIn_0.4s_ease-out]
  "
            >
              <div className="text-lg font-semibold text-blue-700 mb-1">
                {exp.title}
              </div>

              {progress[exp.id] === "completed" ? (
                <div className="flex items-center justify-between mt-2">
                  <span className="text-green-600 text-sm font-medium">
                    {t("chemLab.completed")}
                  </span>
                  <button
                    onClick={() => resetExperiment(exp.id)}
                    className="text-xs bg-blue-600 text-white px-3 py-1 rounded hover:bg-blue-700 transition-all"
                  >
                    {t("chemLab.practiceAgain")}
                  </button>
                </div>
              ) : (
                <button
                  onClick={() =>
                    navigate(
                      `/dashboard/student/chemistry/${exp.id}?class=${selectedClass}`
                    )
                  }
                  className="mt-3 bg-blue-600 text-white px-3 py-1 rounded text-sm hover:bg-blue-700 transition-all"
                >
                 {t("chemLab.launch")}
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
